import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  BookOpen, MapPin, Users, Sparkles, Heart, ArrowRight, ChevronLeft, ChevronRight,
  Camera, Handshake, ShieldCheck, RefreshCcw, Lock, Quote, Star,
  Baby, Puzzle, Rocket, Moon, Library,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, thumbUrl } from "@/lib/api";
import BookCard from "@/components/BookCard";

const HOW_IT_WORKS = [
  { icon: Camera, title: "Photograph a book", body: "Snap a clear photo of any book you own — our AI fills in the title, author, and age group instantly.", tint: "bg-orange-500" },
  { icon: MapPin, title: "Request a nearby book", body: "Browse the shelf near you and send a request with two taps. No back-and-forth needed.", tint: "bg-teal-500" },
  { icon: Handshake, title: "Arrange a safe pickup", body: "Chat with the other family, agree on a public spot nearby, and hand the book over.", tint: "bg-rose-500" },
];

const AGE_TILES = [
  { label: "0–3 years", sub: "Board & picture books", age: "Toddler", icon: Baby, tint: "from-rose-400 to-rose-500" },
  { label: "4–6 years", sub: "Preschool favourites", age: "Preschool", icon: Puzzle, tint: "from-amber-400 to-orange-500" },
  { label: "7–9 years", sub: "Early readers", age: "Early Reader", icon: BookOpen, tint: "from-teal-400 to-teal-500" },
  { label: "10–13 years", sub: "Middle-grade adventures", age: "Middle Grade", icon: Rocket, tint: "from-sky-400 to-blue-500" },
  { label: "13–18 years", sub: "Young adult reads", age: "Young Adult", icon: Moon, tint: "from-violet-400 to-purple-500" },
  { label: "Above 18 years", sub: "Novels, cookbooks & more", age: "all", icon: Library, tint: "from-slate-500 to-slate-700" },
];

const TRUST_ITEMS = [
  { icon: ShieldCheck, title: "Verified Families", body: "Every parent signs up with a verified email and profile before they can borrow or lend a single book." },
  { icon: RefreshCcw, title: "Return Tracking", body: "Every rental has a due date, gentle reminders, and a ledger that tracks the handover both ways." },
  { icon: Lock, title: "Privacy Protection", body: "Your exact address is never shown to anyone — only your neighbourhood, until you choose to connect." },
];

function CountUp({ value, prefix = "", suffix = "", duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const visibleRef = useRef(false);
  const valueRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => { valueRef.current = value; }, [value]);

  const animateTo = (target) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      setDisplay(Math.round(progress * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // Observe visibility once. Reads the latest value via a ref (not the closed-over
  // prop) so a slow API response that resolves after the section is already
  // visible still animates to the correct number.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setDisplay(valueRef.current); return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !visibleRef.current) {
        visibleRef.current = true;
        animateTo(valueRef.current);
      }
    }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [duration]);

  // If the real value arrives after the counter is already visible (e.g. the
  // section was in view on load, before /api/stats/impact responded), re-animate.
  useEffect(() => {
    if (visibleRef.current) animateTo(value);
  }, [value, duration]);

  return <span ref={ref}>{prefix}{display.toLocaleString("en-IN")}{suffix}</span>;
}

function FeaturedSlider({ category, books }) {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.round(el.clientWidth * 0.85) * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: step, behavior: "smooth" });
  };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={`featured-slider-${category.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-1 flex items-center gap-2">
            <Sparkles size={14} /> Featured
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
            Top {category}s this week
          </h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={() => scrollBy("left")}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-amber-50 flex items-center justify-center"
            aria-label="Scroll left"
            data-testid={`featured-prev-${category.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollBy("right")}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-amber-50 flex items-center justify-center"
            aria-label="Scroll right"
            data-testid={`featured-next-${category.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {books.map((b) => (
          <div
            key={b.book_id}
            className="min-w-[220px] w-[220px] sm:w-[240px] snap-start flex-shrink-0"
            data-testid={`featured-tile-${b.book_id}`}
          >
            <BookCard book={b} />
          </div>
        ))}
      </div>
    </section>
  );
}

function ParentStories({ testimonials }) {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.round(el.clientWidth * 0.85) * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: step, behavior: "smooth" });
  };
  if (testimonials.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="parent-stories">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div className="max-w-2xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-2 flex items-center gap-2">
            <Quote size={14} /> Parent stories
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            What families on the shelf are saying.
          </h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={() => scrollBy("left")}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-amber-50 flex items-center justify-center"
            aria-label="Scroll left"
            data-testid="parent-stories-prev"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollBy("right")}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-amber-50 flex items-center justify-center"
            aria-label="Scroll right"
            data-testid="parent-stories-next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {testimonials.map((t) => (
          <div
            key={t.testimonial_id}
            className="min-w-[280px] w-[280px] sm:w-[320px] snap-start flex-shrink-0 card-soft p-6 flex flex-col"
            data-testid={`testimonial-${t.testimonial_id}`}
          >
            <Quote size={22} className="text-orange-400 mb-3" strokeWidth={2} />
            <div className="flex items-center gap-0.5 text-amber-500 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={14} fill={i < (t.rating || 5) ? "currentColor" : "none"} strokeWidth={i < (t.rating || 5) ? 0 : 1.5} />
              ))}
            </div>
            <p className="text-slate-700 leading-relaxed text-sm flex-1">&ldquo;{t.quote}&rdquo;</p>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="font-display font-semibold text-slate-900">{t.parent_name}</div>
              <div className="text-xs text-slate-500">{t.community_label}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [impact, setImpact] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/featured-books")
      .then(({ data }) => setFeatured((data?.categories || []).slice(0, 1)))
      .catch(() => setFeatured([]));
    api.get("/books", { params: { limit: 3 } })
      .then(({ data }) => setTrending(Array.isArray(data) ? data.slice(0, 3) : []))
      .catch(() => setTrending([]));
    api.get("/stats/impact")
      .then(({ data }) => setImpact(data))
      .catch(() => setImpact(null));
    api.get("/testimonials")
      .then(({ data }) => setTestimonials(Array.isArray(data) ? data : []))
      .catch(() => setTestimonials([]));
  }, []);

  return (
    <main className="fade-in overflow-x-hidden">
      {/* 100% free community banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-testid="homepage-free-banner">
        <div className="rounded-2xl border border-teal-200 bg-teal-50 text-teal-900 px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 flex-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-teal-500 text-white text-xs font-bold flex-shrink-0">₹0</span>
            <div className="text-sm leading-snug">
              <b>JuniorShelf is 100% free</b> — no money required, just community sharing.
            </div>
          </div>
          <Link
            to="/browse"
            className="btn-primary text-sm py-2.5 px-5 whitespace-nowrap self-stretch sm:self-auto justify-center bg-orange-500 hover:bg-orange-600 shadow-[0_10px_30px_-10px_rgba(249,115,22,0.6)]"
            data-testid="top-cta-borrow-first-book"
            onClick={() => window.gtag && window.gtag('event', 'browse_books_click', { location: 'top_cta' })}
          >
            Browse Books <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 space-y-6 slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-amber-200 text-xs font-bold uppercase tracking-[0.2em] text-orange-500 shadow-sm">
              <Sparkles size={14} /> Neighbourhood book club
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]" data-testid="landing-h1">
              Borrow books instantly from your
              {" "}<span className="text-orange-500">neighbours for free</span>.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Junior Shelf is a shared digital shelf for every book in your home —
              picture books, novels, cookbooks, textbooks. Lend the ones you've outgrown,
              borrow the next great read from a neighbour.
            </p>

            {/* Trending Near You — 3-book preview so value shows above the fold */}
            {trending.length > 0 && (
              <div className="pt-1" data-testid="trending-near-you">
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500 mb-2">Trending near you</div>
                <div className="grid grid-cols-3 gap-2">
                  {trending.map((b) => (
                    <Link
                      key={b.book_id}
                      to={`/book/${b.book_id}`}
                      className="group rounded-2xl overflow-hidden bg-white border border-amber-100 hover:-translate-y-0.5 transition-transform"
                      data-testid={`trending-tile-${b.book_id}`}
                    >
                      <div className="aspect-[3/4] bg-amber-100 flex items-center justify-center overflow-hidden">
                        {b.cover_urls?.[0] ? (
                          <img src={thumbUrl(b.cover_urls[0])} alt={b.title} loading="lazy" className="w-full h-full object-contain group-hover:scale-[1.04] transition-transform" />
                        ) : (
                          <BookOpen size={22} className="text-amber-400" />
                        )}
                      </div>
                      <div className="p-2">
                        <div className="text-xs font-semibold text-slate-900 line-clamp-1">{b.title}</div>
                        {typeof b.distance_km === "number" && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{b.distance_km.toFixed(1)} km away</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to={user ? "/browse" : "/register"}
                className="btn-primary text-base px-6 py-3 shadow-[0_10px_30px_-10px_rgba(249,115,22,0.55)] bg-orange-500 hover:bg-orange-600"
                data-testid="hero-cta-primary"
                onClick={() => window.gtag && window.gtag('event','join_button_click',{location:'hero_primary'})}
              >
                {user ? "Browse books" : "Join Now"}
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/browse"
                className="btn-outline"
                data-testid="hero-cta-browse"
              >
                <BookOpen size={18} /> Browse Now
              </Link>
            </div>
            <div className="flex items-center gap-6 pt-6 text-sm text-slate-500">
              <div className="flex -space-x-3">
                {["https://images.pexels.com/photos/6692846/pexels-photo-6692846.jpeg?auto=compress&cs=tinysrgb&w=100",
                  "https://images.pexels.com/photos/8342202/pexels-photo-8342202.jpeg?auto=compress&cs=tinysrgb&w=100",
                  "https://images.pexels.com/photos/9705884/pexels-photo-9705884.jpeg?auto=compress&cs=tinysrgb&w=100"
                ].map((u, i) => (
                  <img key={u} src={u} alt="" className="w-9 h-9 rounded-full border-2 border-amber-50 object-cover" />
                ))}
              </div>
              <div>
                <div className="font-semibold text-slate-900">150+ families</div>
                <div className="text-xs">sharing stories on Junior Shelf</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 relative slide-up">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_70px_-30px_rgba(15,23,42,0.35)] rotate-1">
              <picture>
                <source srcSet="/home-family.webp" type="image/webp" />
                <img
                  src="/monica-family.jpeg"
                  alt="Parent reading with child"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                  width="825"
                  height="520"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 via-transparent to-transparent" />
            </div>
            <div className="hidden md:block absolute -left-6 bottom-8 bg-white rounded-2xl border border-slate-100 shadow-xl px-4 py-3 rotate-[-4deg]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-500">Just returned</div>
              <div className="text-sm font-semibold text-slate-900">Sudha Murthy</div>
              <div className="text-xs text-slate-500">Loved by Trisha, 8 · 5 stars</div>
            </div>
            <div className="hidden md:block absolute -right-4 top-6 bg-teal-500 text-white rounded-2xl shadow-xl px-4 py-3 rotate-[3deg]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">Free to borrow</div>
              <div className="text-sm font-semibold">2 kms away</div>
            </div>
          </div>
        </div>
      </section>

      {/* Age Discovery — visual category tiles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8" data-testid="age-discovery">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-2">Shop by age</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            Find the right shelf for every age.
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {AGE_TILES.map((t) => (
            <Link
              key={t.label}
              to={t.age === "all" ? "/browse" : `/browse?age=${encodeURIComponent(t.age)}`}
              className={`group relative overflow-hidden rounded-3xl p-4 sm:p-5 text-white bg-gradient-to-br ${t.tint} hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
              data-testid={`age-tile-${t.age.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <t.icon size={20} strokeWidth={2.2} />
              </div>
              <div className="font-display font-semibold leading-tight">{t.label}</div>
              <div className="text-[11px] text-white/80 mt-1">{t.sub}</div>
              <ArrowRight size={14} className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </section>

      {/* Featured sliders (Gemini-curated, refreshed daily) */}
      {featured.length > 0 && (
        <div className="bg-gradient-to-b from-transparent to-amber-50/60" data-testid="featured-strip">
          {featured.map((cat) => (
            <FeaturedSlider key={cat.category} category={cat.category} books={cat.books || []} />
          ))}
        </div>
      )}

      {/* How it works — interactive 3-step guide */}
      <section className="bg-white/60 backdrop-blur-sm border-y border-amber-100/60 py-16 md:py-20" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-2">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              Three simple steps between your shelf and theirs.
            </h2>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-orange-300 via-teal-300 to-rose-300" aria-hidden="true" />
            {HOW_IT_WORKS.map((s, i) => (
              <div key={s.title} className="relative group" data-testid={`how-it-works-step-${i}`}>
                <div className={`relative z-10 w-16 h-16 ${s.tint} rounded-3xl flex items-center justify-center text-white shadow-lg mb-5 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300`}>
                  <s.icon size={26} strokeWidth={2.2} />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-slate-900 text-xs font-bold flex items-center justify-center border-2 border-amber-100">{i + 1}</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Impact */}
      {/* <section className="bg-gradient-to-b from-amber-50/60 to-transparent py-16 md:py-20" data-testid="trust-impact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-2">Built on trust</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              Safe swaps, real neighbours, real impact.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-testid="trust-strip">
            {TRUST_ITEMS.map((t) => (
              <div key={t.title} className="card-soft p-6 flex items-start gap-4" data-testid={`trust-item-${t.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 rounded-2xl bg-teal-500 text-white flex items-center justify-center flex-shrink-0">
                  <t.icon size={20} strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-display font-semibold text-slate-900">{t.title}</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-10 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 relative overflow-hidden" data-testid="impact-counter">
            <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-orange-500/20 blur-3xl" aria-hidden="true" />
            <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-teal-500/20 blur-3xl" aria-hidden="true" />
            <div className="relative z-10 text-center sm:text-left" data-testid="impact-books-shared">
              <div className="font-display text-3xl sm:text-4xl font-semibold">
                <CountUp value={impact?.books_shared ?? 0} suffix="+" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">Books Shared</div>
            </div>
            <div className="relative z-10 text-center sm:text-left" data-testid="impact-money-saved">
              <div className="font-display text-3xl sm:text-4xl font-semibold">
                <CountUp value={impact?.money_saved_inr ?? 0} prefix="₹" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">Money Saved</div>
            </div>
            <div className="relative z-10 text-center sm:text-left" data-testid="impact-active-neighbourhoods">
              <div className="font-display text-3xl sm:text-4xl font-semibold">
                <CountUp value={impact?.active_neighbourhoods ?? 0} suffix="+" />
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">Active Neighbourhoods</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Community */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="https://images.pexels.com/photos/8342202/pexels-photo-8342202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1100"
              alt="Kid reading in a library"
              className="w-full h-[440px] object-cover rounded-3xl shadow-[0_20px_50px_-25px_rgba(15,23,42,0.4)]"
            />
            <div className="absolute -bottom-6 -right-6 bg-amber-50 rounded-3xl shadow-xl px-6 py-5 border border-amber-100 max-w-[240px]">
              <div className="flex items-center gap-1 text-amber-500 mb-1">
                {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
              </div>
              <p className="text-sm text-slate-700 font-medium leading-relaxed">
                "My daughter went through 12 books last month — and I only paid for coffee."
              </p>
              <div className="text-xs text-slate-500 mt-2">— Priya, Gurgaon</div>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500 mb-3 flex items-center gap-2">
              <Heart size={14} fill="currentColor" strokeWidth={0} /> Why families love it
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6">
              Less clutter. More stories.
            </h2>
            <ul className="space-y-4 text-slate-700">
              {[
                "Rescue books from the donation pile — pass them to another family who'll love them next.",
                "Borrow locally instead of buying every new bedtime obsession.",
                "Meet the neighbors your kids already share a park with.",
                "Every rental is capped by weeks, tracked in your ledger, and gently reminded 2 days before return.",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user ? "/list" : "/register"} className="btn-primary" data-testid="community-cta-list">
                List your first book
              </Link>
              <Link to="/browse" className="btn-outline" data-testid="community-cta-browse">
                Just browsing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Stories — admin-editable testimonials */}
      <ParentStories testimonials={testimonials} />
    </main>
  );
}
